(() => {
if (customElements.get("brand-collection-rail")) return;

class BrandCollectionRail extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedToRail) return;

    this.tablist = this.querySelector("[data-collection-rail-tabs]");
    this.controls = this.querySelector("[data-collection-rail-controls]");
    this.previousButton = this.querySelector("[data-collection-rail-previous]");
    this.nextButton = this.querySelector("[data-collection-rail-next]");
    this.tabs = Array.from(this.querySelectorAll("[data-collection-rail-tab]"));
    this.panels = Array.from(this.querySelectorAll("[data-collection-rail-panel]"));
    this.viewports = Array.from(this.querySelectorAll("[data-collection-rail-viewport]"));
    this.viewport = this.querySelector("[data-collection-rail-panel]:not([hidden]) [data-collection-rail-viewport]");

    if (!this.tablist || !this.tabs.length || !this.panels.length) return;

    this.isConnectedToRail = true;
    this.updateControls = this.updateControls.bind(this);
    this.handlePrevious = () => this.scroll(-1);
    this.handleNext = () => this.scroll(1);
    this.handleTabClick = (event) => {
      const tab = event.target.closest?.("[data-collection-rail-tab]");
      if (!tab || !this.tablist.contains(tab)) return;

      this.activateTab(tab.dataset.collectionRailTab);
    };
    this.handleTabKeydown = (event) => this.onTabKeydown(event);
    this.handleBlockSelect = (event) => this.onBlockSelect(event);
    this.handleResize = () => {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.centerActiveTab("auto");
        this.updateControls();
      });
    };

    this.previousButton?.addEventListener("click", this.handlePrevious);
    this.nextButton?.addEventListener("click", this.handleNext);
    this.tablist.addEventListener("click", this.handleTabClick);
    this.tablist.addEventListener("keydown", this.handleTabKeydown);
    this.viewports.forEach((viewport) => {
      viewport.addEventListener("scroll", this.updateControls, { passive: true });
    });
    document.addEventListener("shopify:block:select", this.handleBlockSelect);
    window.addEventListener("resize", this.handleResize);

    this.centerActiveTab("auto");
    this.updateControls();
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener("click", this.handlePrevious);
    this.nextButton?.removeEventListener("click", this.handleNext);
    this.tablist?.removeEventListener("click", this.handleTabClick);
    this.tablist?.removeEventListener("keydown", this.handleTabKeydown);
    this.viewports?.forEach((viewport) => {
      viewport.removeEventListener("scroll", this.updateControls);
    });
    document.removeEventListener("shopify:block:select", this.handleBlockSelect);
    window.removeEventListener("resize", this.handleResize);
    window.cancelAnimationFrame(this.controlsFrame);
    window.cancelAnimationFrame(this.resizeFrame);
    this.isConnectedToRail = false;
  }

  activateTab(tabKey, focus = false, behavior = "smooth") {
    const nextTab = this.tabs.find((tab) => tab.dataset.collectionRailTab === tabKey);
    const nextPanel = this.panels.find((panel) => panel.dataset.collectionRailPanel === tabKey);
    if (!nextTab || !nextPanel) return;

    this.tabs.forEach((tab) => {
      const isActive = tab === nextTab;
      tab.classList.toggle("is-active", isActive);
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel !== nextPanel;
    });

    this.viewport = nextPanel.querySelector("[data-collection-rail-viewport]");
    if (focus) nextTab.focus({ preventScroll: true });
    this.centerTab(nextTab, behavior);
    this.updateControls();
  }

  onTabKeydown(event) {
    const currentTab = event.target.closest?.("[data-collection-rail-tab]");
    const currentIndex = this.tabs.indexOf(currentTab);
    let nextIndex;

    if (currentIndex < 0) return;
    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = this.tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    this.activateTab(this.tabs[nextIndex].dataset.collectionRailTab, true);
  }

  onBlockSelect(event) {
    const blockId = event.detail?.blockId;
    const eventTarget = event.target;
    let selectedTab;

    if (eventTarget && this.contains(eventTarget)) {
      selectedTab = eventTarget.closest?.("[data-collection-rail-tab]");
      if (!selectedTab || !this.tablist.contains(selectedTab)) {
        selectedTab = eventTarget.querySelector?.("[data-collection-rail-tab]");
      }
    }

    if (!selectedTab && blockId) {
      selectedTab = this.tabs.find((tab) => tab.dataset.collectionRailBlockId === blockId);
    }

    if (!selectedTab || !this.tablist.contains(selectedTab)) return;
    this.activateTab(selectedTab.dataset.collectionRailTab);
  }

  centerActiveTab(behavior = "smooth") {
    const activeTab = this.tabs.find((tab) => tab.getAttribute("aria-selected") === "true");
    if (activeTab) this.centerTab(activeTab, behavior);
  }

  centerTab(tab, behavior = "smooth") {
    if (!this.tablist || !tab || !this.tablist.contains(tab)) return;

    const maximumScroll = Math.max(0, this.tablist.scrollWidth - this.tablist.clientWidth);
    const tablistBounds = this.tablist.getBoundingClientRect();
    const tabBounds = tab.getBoundingClientRect();
    const centeredPosition = this.tablist.scrollLeft
      + tabBounds.left
      + tabBounds.width / 2
      - tablistBounds.left
      - tablistBounds.width / 2;
    const left = Math.min(maximumScroll, Math.max(0, centeredPosition));
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.tablist.scrollTo({
      behavior: behavior === "smooth" && !reduceMotion ? "smooth" : "auto",
      left
    });
  }

  scroll(direction) {
    if (!this.viewport) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const grid = this.viewport.querySelector(".brand-product-rail__grid");
    const item = grid?.querySelector(":scope > li");
    const gap = grid ? parseFloat(window.getComputedStyle(grid).columnGap) || 0 : 0;
    const distance = item ? item.getBoundingClientRect().width + gap : Math.max(280, this.viewport.clientWidth * 0.86);

    this.viewport.scrollBy({
      behavior: reduceMotion ? "auto" : "smooth",
      left: direction * distance
    });
  }

  updateControls() {
    if (!this.viewport || this.controlsFrame) return;

    this.controlsFrame = window.requestAnimationFrame(() => {
      this.controlsFrame = null;
      const maximumScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      const hasOverflow = maximumScroll > 2;
      if (this.controls) this.controls.hidden = !hasOverflow;
      this.viewport.tabIndex = hasOverflow ? 0 : -1;
      if (this.previousButton) this.previousButton.disabled = this.viewport.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = this.viewport.scrollLeft >= maximumScroll - 2;
    });
  }
}

customElements.define("brand-collection-rail", BrandCollectionRail);
})();
